import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import type { Tag, Vehicle } from "@/interfaces";
import { cn, errorHandler } from "@/lib/utils";
import { tagService } from "@/services/api/tags";
import { vehicleService } from "@/services/api/vehicles";
import { Check, ChevronDown, ChevronRight, ChevronsUpDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "./button";
import { FormControl } from "./form";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Skeleton } from "./skeleton";

interface VehicleSelectProps {
  value?: number | number[];
  onChange: (value: number | number[]) => void;
  onSelectedVehiclesChange?: (vehicles: Vehicle[]) => void;
  onVehiclesLoaded?: (vehicles: Vehicle[]) => void;
  userId?: number;
  multiple?: boolean;
  supportsVideo?: boolean;
  supportsCan?: boolean;
}

const VehicleSelect = ({
  value,
  onChange,
  onSelectedVehiclesChange,
  onVehiclesLoaded,
  userId,
  multiple = false,
  supportsVideo = false,
  supportsCan = false,
}: VehicleSelectProps) => {
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [taggedVehicles, setTaggedVehicles] = useState<Tag[]>([]);
  const [vehiclesWithNoTags, setVehiclesWithNoTags] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [expandedTags, setExpandedTags] = useState<number[]>([]);

  const onVehiclesLoadedRef = useRef(onVehiclesLoaded);
  onVehiclesLoadedRef.current = onVehiclesLoaded;

  useEffect(() => {
    const loadVehiclesWithTags = async () => {
      try {
        setLoading(true);
        let response;
        let responseData;

        if (supportsCan) {
          response = await vehicleService.loadVehiclesWithCanTelemetry();
          responseData = response;
        } else if (supportsVideo) {
          response = await vehicleService.loadVehiclesWithVideo();
          responseData = response;
        } else {
          response = await tagService.getTagAndVehicleInfo();
          responseData = response.data;
        }

        let tags: Tag[] = [];
        let vehiclesWithNoTags: Vehicle[] = [];

        if ("tags" in responseData && "vehicles_with_no_tags" in responseData) {
          tags = responseData.tags || [];
          vehiclesWithNoTags = responseData.vehicles_with_no_tags || [];
        }

        const allVehicles = [
          ...tags.flatMap((tag) => tag.vehicle || []),
          ...vehiclesWithNoTags,
        ];

        const processedVehicles = allVehicles
          .filter((vehicle): vehicle is Vehicle => vehicle !== undefined)
          .map((vehicle) => ({
            ...vehicle,
            label: `${vehicle.plate} ${vehicle.title}`,
          }));

        setVehicles(processedVehicles);
        onVehiclesLoadedRef.current?.(processedVehicles);
        setTaggedVehicles(tags);
        setVehiclesWithNoTags(
          vehiclesWithNoTags.map((vehicle: Vehicle) => ({
            ...vehicle,
            label: `${vehicle.plate} ${vehicle.title}`,
          })),
        );

        setExpandedTags(tags.map((tag) => tag.id));
      } catch (err) {
        errorHandler(err, "Erro ao carregar veículos e grupos.");
      } finally {
        setLoading(false);
      }
    };

    loadVehiclesWithTags();
  }, [supportsCan, supportsVideo, userId]);

  const handleSelect = (vehicleId: number) => {
    if (multiple) {
      const currentValues = Array.isArray(value) ? value : [];
      const newValues = currentValues.includes(vehicleId)
        ? currentValues.filter((id) => id !== vehicleId)
        : [...currentValues, vehicleId];
      handleChange(newValues);
    } else {
      handleChange(vehicleId);
      setOpen(false);
    }
  };

  const handleChange = (newValue: number | number[]) => {
    onChange(newValue);

    const selectedIds = Array.isArray(newValue) ? newValue : [newValue];
    const selectedVehicles = Array.from(
      new Map(
        vehicles
          .filter((vehicle) => selectedIds.includes(vehicle.id))
          .map((vehicle) => [vehicle.id, vehicle]),
      ).values(),
    );
    onSelectedVehiclesChange?.(selectedVehicles);
  };

  const handleSelectAll = () => {
    if (multiple) {
      const allVehicleIds = vehicles.map((vehicle) => vehicle.id);
      handleChange([...new Set(allVehicleIds)]);
    }
  };

  const handleDeselectAll = () => {
    if (multiple) {
      handleChange([]);
    }
  };

  const toggleTagExpansion = (tagId: number) => {
    setExpandedTags((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId],
    );
  };

  const handleSelectTagVehicles = (tagVehicles: Vehicle[]) => {
    if (multiple) {
      const tagVehicleIds = tagVehicles.map((vehicle) => vehicle.id);
      const currentValues = Array.isArray(value) ? value : [];

      const allSelected = tagVehicleIds.every((id) =>
        currentValues.includes(id),
      );

      if (allSelected) {
        const newValues = currentValues.filter(
          (id) => !tagVehicleIds.includes(id),
        );
        handleChange(newValues);
      } else {
        const newValues = [...new Set([...currentValues, ...tagVehicleIds])];
        handleChange(newValues);
      }
    }
  };

  const getSelectedLabel = () => {
    if (multiple) {
      const selectedIds = Array.isArray(value) ? value : [];

      const uniqueVehiclesMap = new Map();
      vehicles.forEach((vehicle) => {
        if (
          selectedIds.includes(vehicle.id) &&
          !uniqueVehiclesMap.has(vehicle.id)
        ) {
          uniqueVehiclesMap.set(vehicle.id, vehicle);
        }
      });

      const uniqueSelectedVehicles = Array.from(uniqueVehiclesMap.values());

      return uniqueSelectedVehicles.length > 0
        ? uniqueSelectedVehicles.length === 1
          ? uniqueSelectedVehicles[0].label
          : `${uniqueSelectedVehicles.length} veículos selecionados`
        : "Selecione os veículos";
    } else {
      if (!value || (Array.isArray(value) && value.length === 0))
        return "Selecione um veículo";
      return value
        ? vehicles.find((vehicle) => vehicle.id === value)?.label
        : "Selecione um veículo";
    }
  };

  const isTagFullySelected = (vehicles: Vehicle[]) => {
    if (!multiple || !Array.isArray(value)) return false;
    return (
      vehicles.length > 0 &&
      vehicles.every((vehicle) => value.includes(vehicle.id))
    );
  };

  const isTagPartiallySelected = (vehicles: Vehicle[]) => {
    if (!multiple || !Array.isArray(value)) return false;
    return (
      !isTagFullySelected(vehicles) &&
      vehicles.some((vehicle) => value.includes(vehicle.id))
    );
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full bg-muted" />
        <Popover>
          <PopoverContent className="w-full p-2">
            <div className="space-y-2">
              <Skeleton className="h-8 w-full bg-muted" />
              <Skeleton className="h-8 w-full bg-muted" />
              <Skeleton className="h-8 w-full bg-muted" />
            </div>
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FormControl>
          <Button
            data-testid="vehicle-select-trigger"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={loading}
            className={cn(
              "w-full justify-between overflow-hidden",
              (!value ||
                (multiple && Array.isArray(value) && value.length === 0)) &&
                "text-muted-foreground",
              "dark:border-white",
            )}
          >
            <span className="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap min-w-0">
              {getSelectedLabel()}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50 shrink-0" />
          </Button>
        </FormControl>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command
          filter={(value, search) => {
            if (value.toLowerCase().includes(search.toLowerCase())) return 1;
            return 0;
          }}
        >
          <CommandInput placeholder="Procure um veículo..." className="h-9" />
          <CommandList>
            <CommandEmpty>Nenhum veículo encontrado.</CommandEmpty>
            {multiple && (
              <CommandGroup>
                <div className="flex gap-2 p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-start"
                    onClick={handleSelectAll}
                  >
                    Marcar todos
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="flex-1 justify-start"
                    onClick={handleDeselectAll}
                  >
                    Desmarcar todos
                  </Button>
                </div>
              </CommandGroup>
            )}

            {taggedVehicles.map((tag) => (
              <div key={tag.id}>
                <CommandGroup>
                  <div
                    className={cn(
                      "flex items-center px-2 py-1.5 text-sm font-medium cursor-pointer hover:bg-accent",
                      multiple && "group",
                    )}
                    onClick={() => toggleTagExpansion(tag.id)}
                  >
                    {expandedTags.includes(tag.id) ? (
                      <ChevronDown className="mr-1 h-4 w-4" />
                    ) : (
                      <ChevronRight className="mr-1 h-4 w-4" />
                    )}
                    {tag.value.toUpperCase()}
                    {multiple && (
                      <div
                        className="ml-auto flex items-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (tag.vehicle) {
                            handleSelectTagVehicles(tag.vehicle);
                          }
                        }}
                      >
                        <Check
                          className={cn(
                            "transition-opacity",
                            tag.vehicle && isTagFullySelected(tag.vehicle)
                              ? "opacity-100"
                              : tag.vehicle &&
                                  isTagPartiallySelected(tag.vehicle)
                                ? "opacity-50"
                                : "opacity-0 group-hover:opacity-30",
                          )}
                        />
                      </div>
                    )}
                  </div>
                </CommandGroup>

                {expandedTags.includes(tag.id) &&
                  (tag.vehicle?.length ?? 0) > 0 && (
                    <CommandGroup className="pl-6">
                      {tag.vehicle?.map((vehicle: Vehicle) => (
                        <CommandItem
                          value={`${vehicle.plate} ${vehicle.title}`}
                          key={vehicle.id}
                          onSelect={() => handleSelect(vehicle.id)}
                        >
                          {vehicle.plate} {vehicle.title}
                          <Check
                            className={cn(
                              "ml-auto",
                              multiple
                                ? Array.isArray(value) &&
                                  value.includes(vehicle.id)
                                  ? "opacity-100"
                                  : "opacity-0"
                                : vehicle.id === value
                                  ? "opacity-100"
                                  : "opacity-0",
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                <CommandSeparator />
              </div>
            ))}

            {vehiclesWithNoTags.length > 0 && (
              <>
                <CommandGroup>
                  <div
                    className={cn(
                      "flex items-center px-2 py-1.5 text-sm font-medium cursor-pointer hover:bg-accent",
                      multiple && "group",
                    )}
                  >
                    VEÍCULOS SEM GRUPO
                    {multiple && (
                      <div
                        className="ml-auto flex items-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectTagVehicles(vehiclesWithNoTags);
                        }}
                      >
                        <Check
                          className={cn(
                            "transition-opacity",
                            isTagFullySelected(vehiclesWithNoTags)
                              ? "opacity-100"
                              : isTagPartiallySelected(vehiclesWithNoTags)
                                ? "opacity-50"
                                : "opacity-0 group-hover:opacity-30",
                          )}
                        />
                      </div>
                    )}
                  </div>
                </CommandGroup>
                <CommandGroup className="pl-6">
                  {vehiclesWithNoTags.map((vehicle) => (
                    <CommandItem
                      value={vehicle.label}
                      key={vehicle.id}
                      onSelect={() => handleSelect(vehicle.id)}
                    >
                      {vehicle.plate} {vehicle.title}
                      <Check
                        className={cn(
                          "ml-auto",
                          multiple
                            ? Array.isArray(value) && value.includes(vehicle.id)
                              ? "opacity-100"
                              : "opacity-0"
                            : vehicle.id === value
                              ? "opacity-100"
                              : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export default VehicleSelect;
